import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, ArrowLeft } from "lucide-react";

const SPECIALTIES = ["clothing", "footwear", "accessory"];
const TIERS = ["Independent designer", "Small-run manufacturer", "Manufacturer", "Atelier", "Jeweler", "Milliner", "Print & DTG house"];
const PRICE = ["$", "$$", "$$$", "$$$$"];

export default function ApplyMaker() {
  const [form, setForm] = useState({
    name: "", email: "", city: "", tier: "Independent designer",
    specialties: ["clothing"], bio: "", portfolio_url: "",
    moq: 1, lead_time_days: 30, price_range: "$$",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const toggleSpec = (s) => setForm((f) => ({
    ...f,
    specialties: f.specialties.includes(s) ? f.specialties.filter(x => x !== s) : [...f.specialties, s],
  }));

  const submit = async () => {
    if (!form.name || !form.email || !form.city || !form.bio || form.specialties.length === 0) {
      toast.error("Fill every field");
      return;
    }
    setBusy(true);
    try {
      await api.post("/maker-applications", form);
      setSent(true);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950 pt-24 pb-24">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <Link to="/" className="overline flex items-center gap-2 text-zinc-500 hover:text-lime-600 transition-colors mb-8" data-testid="apply-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Link>

        <div className="mb-10">
          <div className="overline text-zinc-500 mb-3">§ 04 — Apply to The Bureau</div>
          <h1 className="font-serif text-5xl md:text-7xl leading-[0.9]">Join the makers,<br /><em className="italic font-light">get commissioned.</em></h1>
          <p className="mt-6 max-w-2xl text-lg text-zinc-700 leading-relaxed">
            Independent designers, ateliers, manufacturers, milliners, and leather workshops — apply to receive quote requests from creators who&apos;ve rendered their looks in InstaLux.
          </p>
        </div>

        {sent ? (
          <div className="border border-lime-400 bg-lime-400/10 p-10 flex items-start gap-4" data-testid="apply-sent-view">
            <CheckCircle2 className="w-8 h-8 text-lime-600 shrink-0" />
            <div>
              <div className="font-serif text-3xl">Received.</div>
              <p className="text-zinc-700 mt-2 leading-relaxed">We&apos;ll review your portfolio and email you at <span className="font-mono">{form.email}</span> within a few days. If approved, you&apos;ll appear in the Bureau and start receiving commissions.</p>
              <Link to="/" className="mt-6 inline-flex overline text-lime-700 hover:text-zinc-950" data-testid="apply-home-link">Back to the studio →</Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="apply-form">
            <Field label="Studio / Brand name">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-zinc-300 bg-white p-3 focus:border-lime-500 focus:outline-none" data-testid="apply-name-input" />
            </Field>
            <Field label="Contact email">
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-zinc-300 bg-white p-3 focus:border-lime-500 focus:outline-none" data-testid="apply-email-input" />
            </Field>
            <Field label="City">
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Paris, FR" className="w-full border border-zinc-300 bg-white p-3 focus:border-lime-500 focus:outline-none" data-testid="apply-city-input" />
            </Field>
            <Field label="Tier">
              <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="w-full border border-zinc-300 bg-white p-3 focus:border-lime-500 focus:outline-none" data-testid="apply-tier-select">
                {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>

            <div className="md:col-span-2">
              <div className="overline text-zinc-500 mb-2">Specialties</div>
              <div className="flex gap-1">
                {SPECIALTIES.map((s) => (
                  <button
                    type="button" key={s}
                    onClick={() => toggleSpec(s)}
                    className={`flex-1 py-3 overline border ${form.specialties.includes(s) ? "bg-lime-400 border-lime-400 text-zinc-950" : "border-zinc-300 hover:border-zinc-950"} transition-colors`}
                    data-testid={`apply-specialty-${s}`}
                  >{s}</button>
                ))}
              </div>
            </div>

            <Field label="Bio (what you make, materials, philosophy)" full>
              <textarea rows={4} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="w-full border border-zinc-300 bg-white p-3 focus:border-lime-500 focus:outline-none resize-none" data-testid="apply-bio-input" />
            </Field>

            <Field label="Portfolio URL (Instagram, website)" full>
              <input value={form.portfolio_url} onChange={(e) => setForm({ ...form, portfolio_url: e.target.value })} placeholder="https://…" className="w-full border border-zinc-300 bg-white p-3 focus:border-lime-500 focus:outline-none" data-testid="apply-portfolio-input" />
            </Field>

            <Field label="Minimum order (MOQ)">
              <input type="number" min={1} value={form.moq} onChange={(e) => setForm({ ...form, moq: parseInt(e.target.value) || 1 })} className="w-full border border-zinc-300 bg-white p-3 focus:border-lime-500 focus:outline-none" data-testid="apply-moq-input" />
            </Field>
            <Field label="Lead time (days)">
              <input type="number" min={1} value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: parseInt(e.target.value) || 30 })} className="w-full border border-zinc-300 bg-white p-3 focus:border-lime-500 focus:outline-none" data-testid="apply-lead-input" />
            </Field>

            <Field label="Price range" full>
              <div className="flex gap-1">
                {PRICE.map((p) => (
                  <button
                    type="button" key={p}
                    onClick={() => setForm({ ...form, price_range: p })}
                    className={`flex-1 py-3 border font-mono ${form.price_range === p ? "bg-zinc-950 text-zinc-50 border-zinc-950" : "border-zinc-300 hover:border-zinc-950"} transition-colors`}
                    data-testid={`apply-price-${p.length}`}
                  >{p}</button>
                ))}
              </div>
            </Field>

            <div className="md:col-span-2 mt-4">
              <button
                onClick={submit}
                disabled={busy}
                className={`w-full py-4 rounded-full overline flex items-center justify-center gap-2 ${busy ? "bg-zinc-300 text-zinc-500" : "bg-lime-400 text-zinc-950 hover:bg-zinc-950 hover:text-lime-400"} transition-colors`}
                data-testid="apply-submit-btn"
              >
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting</> : <><Send className="w-4 h-4" /> Submit Application</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="overline text-zinc-500 mb-2">{label}</div>
      {children}
    </div>
  );
}
